import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * #664 (F73) — the result of a step before the sign-in: the reset link is
 * sent, the password is reset, the email is verified or not, the account
 * mail is sent, the reset link is not valid, the sign-in ends. Seven screens
 * wrote an icon, a text and a link by hand, in three sizes. Put the panel in
 * an `AuthCard`.
 *
 * `tone` gives the colour of the icon and of the title, and the role of the
 * title and the text: a screen reader reads an error at once (`alert`) and the
 * other results when it is free (`status`). A notice in `children` has its own
 * role. `pulse` is a step that waits (the check of a link, the
 * sign-in with Google). `children` sit between the text and the action: a
 * notice, a button. `action` is the link to the next page.
 */
type Tone = 'info' | 'success' | 'error' | 'neutral';
type Variant = 'primary' | 'outline' | 'text';

const ICON_CLS: Record<Tone, string> = {
  info: 'text-blue-500',
  success: 'text-green-600',
  error: 'text-red-600',
  neutral: 'text-gray-500',
};
const TITLE_CLS: Record<Tone, string> = {
  info: 'text-gray-900',
  success: 'text-green-700',
  error: 'text-red-700',
  neutral: 'text-gray-900',
};
const ACTION_CLS: Record<Variant, string> = {
  primary: 'btn-primary w-full py-2.5',
  outline: 'btn-outline w-full py-2.5',
  text: 'inline-block text-sm text-gray-500 hover:underline',
};

interface AuthResultProps {
  icon?: ComponentType<{ className?: string }>;
  tone?: Tone;
  title?: string;
  text?: ReactNode;
  pulse?: boolean;
  action?: { to: string; label: string; variant?: Variant };
  children?: ReactNode;
}

export default function AuthResult({ icon: Icon, tone = 'neutral', title, text, pulse = false, action, children }: AuthResultProps) {
  return (
    <div className="text-center space-y-3">
      {Icon && (
        <div className={`flex justify-center ${ICON_CLS[tone]}${pulse ? ' animate-pulse' : ''}`}>
          <Icon className="w-12 h-12" />
        </div>
      )}
      {(title || text) && (
        <div role={tone === 'error' ? 'alert' : 'status'} className="space-y-3">
          {title && <h2 className={`text-lg font-bold ${TITLE_CLS[tone]}`}>{title}</h2>}
          {text && <p className="text-sm text-gray-700">{text}</p>}
        </div>
      )}
      {children}
      {action && (
        <Link to={action.to} className={ACTION_CLS[action.variant ?? 'primary']}>{action.label}</Link>
      )}
    </div>
  );
}
